import React from "react";

export type UnitType = "px" | "rem" | "em" | "%" | "vh" | "vw" | "auto";

interface UnitInputProps {
	value: string;
	onChange: (value: string) => void;
	label?: string;
	units?: UnitType[];
	min?: number;
	max?: number;
	step?: number;
	className?: string;
	placeholder?: string;
}

const UnitInput: React.FC<UnitInputProps> = ({
	value,
	onChange,
	label,
	units = ["px", "rem", "em", "%", "vh", "vw", "auto"],
	min,
	max,
	step = 1,
	className = "",
	placeholder = "Value",
}) => {
	// Parse the value to separate the number and unit
	const parseValue = (val: string): [string, UnitType] => {
		if (val === "auto") return ["auto", "auto"];

		const match = val.match(/^([-\d.]+)(\D+)$/);
		if (match) {
			return [match[1], match[2] as UnitType];
		}

		// If no unit is found, default to px
		const numericMatch = val.match(/^([-\d.]+)$/);
		if (numericMatch) {
			return [numericMatch[1], "px"];
		}

		return ["", "px"];
	};

	const [numValue, unitValue] = parseValue(value);

	const handleNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (unitValue === "auto") {
			onChange(e.target.value);
		} else {
			onChange(`${e.target.value}${unitValue}`);
		}
	};

	const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newUnit = e.target.value as UnitType;
		if (newUnit === "auto") {
			onChange("auto");
		} else {
			onChange(`${numValue}${newUnit}`);
		}
	};

	return (
		<div className={`flex flex-col ${className}`}>
			{label && (
				<label className="block text-sm font-medium mb-1">
					{label}
				</label>
			)}
			<div className="flex">
				<input
					type="number"
					min={min}
					max={max}
					step={step}
					value={unitValue === "auto" ? "auto" : numValue}
					onChange={handleNumChange}
					placeholder={placeholder}
					disabled={unitValue === "auto"}
					className="w-full p-2 border rounded-l-md"
				/>
				<select
					value={unitValue}
					onChange={handleUnitChange}
					className="bg-gray-100 border border-l-0 rounded-r-md px-2">
					{units.map((unit) => (
						<option key={unit} value={unit}>
							{unit}
						</option>
					))}
				</select>
			</div>
		</div>
	);
};

export default UnitInput;
